import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import useChainSession from "@/hooks/useChainSession";
import { RefreshCw, Wifi, WifiOff, Clock } from "lucide-react";

function fmt(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Live BSC feed while History is open. Activates a 5-minute scan session;
 * leaving History ends the session and stops the deposit scanner.
 */
export default function ChainStatus() {
  const [chainData, setChainData] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const { active, secondsRemaining, ttl, expired, refresh } = useChainSession();

  const [localSec, setLocalSec] = useState(secondsRemaining);
  const tickRef = useRef(null);

  useEffect(() => {
    setLocalSec(secondsRemaining);
  }, [secondsRemaining]);

  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (active && localSec > 0) {
      tickRef.current = setInterval(() => {
        setLocalSec((s) => Math.max(0, s - 1));
      }, 1000);
    }
    return () => clearInterval(tickRef.current);
  }, [active, localSec > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!active) return undefined;
    const load = async () => {
      try {
        const { data } = await api.get("/api/wallet/chain/status");
        setChainData(data);
        setFetchError(false);
      } catch {
        setFetchError(true);
      }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [active]);

  const pct = ttl > 0 ? Math.round((localSec / ttl) * 100) : 0;
  const isLow = localSec < 60;

  if (expired || (!active && localSec === 0)) {
    return (
      <div className="flex flex-wrap items-center gap-2" data-testid="chain-status">
        <div className="flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-1.5 text-xs text-warning">
          <Clock size={12} className="shrink-0" />
          <span>Live chain feed paused</span>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="flex items-center gap-1.5 rounded-lg border border-border bg-surface-2 px-3 py-1.5 text-xs transition hover:border-primary"
        >
          <RefreshCw size={12} />
          Re-enable live feed (5 min)
        </button>
      </div>
    );
  }

  if (!active) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="chain-status">
        <div className="h-2 w-2 animate-pulse rounded-full bg-muted-foreground" />
        Connecting to BSC…
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex w-max items-center gap-2 rounded-lg border border-down/40 bg-down/10 px-3 py-1.5 text-xs text-down" data-testid="chain-status">
        <WifiOff size={12} />
        Chain status unavailable
      </div>
    );
  }

  const connected = chainData?.connected;
  const blockNum = chainData?.block_number ?? 0;
  const network = chainData?.network ?? "BSC";

  return (
    <div className="flex w-full max-w-xs flex-col gap-2" data-testid="chain-status">
      {connected ? (
        <div className="flex w-max items-center gap-2 rounded-lg border border-up/40 bg-up/10 px-3 py-1.5 text-xs text-up">
          <Wifi size={12} />
          <span className="num">
            {network} · Block #{Number(blockNum).toLocaleString()}
          </span>
        </div>
      ) : (
        <div className="flex w-max items-center gap-2 rounded-lg border border-down/40 bg-down/10 px-3 py-1.5 text-xs text-down">
          <WifiOff size={12} />
          BSC Offline{chainData?.error ? ` · ${chainData.error}` : ""}
        </div>
      )}

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <Clock size={10} />
            Deposit scan
          </span>
          <span className={isLow ? "text-warning font-semibold" : ""}>
            {fmt(localSec)} remaining
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
          <div
            className={`h-full rounded-full transition-all duration-1000 ${isLow ? "bg-warning" : "brand-gradient"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {isLow && (
          <p className="text-[10px] text-warning">
            Scan expires soon —{" "}
            <button type="button" onClick={refresh} className="underline font-semibold hover:text-warning">
              extend 5 min
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
